import { CircularProgress } from '@kinetik/ui-kit';
import React from 'react';

import { COLORS } from 'theme';

import CheckCircleIcon from '../../../../../app/assets/v2/CheckCircleIcon';
import ErrorIcon from '../../../../../app/assets/v2/ErrorIcon';
import InfoIcon from '../../../../../app/assets/v2/InfoIcon';
import WarningIcon from '../../../../../app/assets/v2/WarningIcon';

/**
 * The types of toast notifications. These types are used to determine the color and style of the toast notification.
 */
export const TOAST_NOTIFICATION_TYPES = Object.freeze({
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  SUCCESS: 'success',
  // Long-running work in progress. Pair with autoHide: false — a spinner that
  // vanishes on a timer would imply the work finished.
  LOADING: 'loading',
});

/**
 * The CSS styles for each toast notification type. These styles are directly applied to the toast notification styled component.
 */
export const TOAST_NOTIFICATION_TYPE_STYLES = Object.freeze({
  [TOAST_NOTIFICATION_TYPES.INFO]: {
    backgroundColor: COLORS.blueLight,
    color: COLORS.blueDark,
  },
  [TOAST_NOTIFICATION_TYPES.WARNING]: {
    backgroundColor: COLORS.yellowLight,
    color: COLORS.yellowDark,
  },
  [TOAST_NOTIFICATION_TYPES.ERROR]: {
    backgroundColor: COLORS.redLight,
    color: COLORS.redDark,
  },
  [TOAST_NOTIFICATION_TYPES.SUCCESS]: {
    backgroundColor: COLORS.greenLight,
    color: COLORS.greenDark,
  },
  [TOAST_NOTIFICATION_TYPES.LOADING]: {
    backgroundColor: COLORS.blueLight,
    color: COLORS.blueDark,
  },
});

/**
 * The icons to display for each toast notification type.
 */
export const TOAST_NOTIFICATION_TYPE_ICONS = Object.freeze({
  [TOAST_NOTIFICATION_TYPES.INFO]: (
    <InfoIcon
      sx={{
        color:
          TOAST_NOTIFICATION_TYPE_STYLES[TOAST_NOTIFICATION_TYPES.INFO].color,
      }}
    />
  ),
  [TOAST_NOTIFICATION_TYPES.WARNING]: (
    <WarningIcon
      sx={{
        color:
          TOAST_NOTIFICATION_TYPE_STYLES[TOAST_NOTIFICATION_TYPES.WARNING]
            .color,
      }}
    />
  ),
  [TOAST_NOTIFICATION_TYPES.ERROR]: (
    <ErrorIcon
      sx={{
        color:
          TOAST_NOTIFICATION_TYPE_STYLES[TOAST_NOTIFICATION_TYPES.ERROR].color,
      }}
    />
  ),
  [TOAST_NOTIFICATION_TYPES.SUCCESS]: (
    <CheckCircleIcon
      sx={{
        color:
          TOAST_NOTIFICATION_TYPE_STYLES[TOAST_NOTIFICATION_TYPES.SUCCESS]
            .color,
      }}
    />
  ),
  // `color` is passed explicitly rather than left to ui-kit's default of
  // theme.color.icon.primary, so the spinner matches the toast palette.
  [TOAST_NOTIFICATION_TYPES.LOADING]: (
    <CircularProgress
      size={20}
      thickness={4}
      color={
        TOAST_NOTIFICATION_TYPE_STYLES[TOAST_NOTIFICATION_TYPES.LOADING].color
      }
      ariaLabel="Loading"
    />
  ),
});

/**
 * The number of milliseconds to expect the user to recognize the toast notification. This is for use in calculating the auto-hide duration.
 */
export const TOAST_NOTIFICATION_RECOGNITION_BUFFER_IN_MS = 1500;

/**
 * The number of milliseconds to expect the user to read each character of the toast notification. This is for use in calculating the auto-hide duration.
 */
export const TOAST_NOTIFICATION_CHAR_READ_RATE_IN_MS = 68;
